import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Eye, EyeOff, Pencil, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useNotificationConfig, useSaveNotificationTemplate, useSetNotificationEnabled } from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';

type NotifType = 'epic_launch' | 'reminder' | 'on_demand';

function TemplateEditor({ notificationType }: { notificationType: NotifType }) {
  // t faltaba acá: el placeholder de la línea ~153 tiraba ReferenceError y
  // blanqueaba la página al abrir el editor (Editar / Crear uno).
  const { t } = useTranslation();
  const { data: config, isLoading: loadingConfig } = useNotificationConfig(notificationType);
  const { data: activeTemplate, isLoading: loadingTemplate } = trpc.performance.notificationTemplates.getActive.useQuery(
    { notificationType },
    { enabled: true },
  );
  const saveTemplate = useSaveNotificationTemplate();
  const setEnabled = useSetNotificationEnabled();

  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [subjectLine, setSubjectLine] = useState('');

  // Pre-fill editor with current template when it loads or editing opens
  useEffect(() => {
    if (editing && activeTemplate) {
      setHtmlContent(activeTemplate.htmlContent ?? '');
      setSubjectLine(activeTemplate.subjectLine ?? '');
    }
  }, [editing, activeTemplate]);

  const isLoading = loadingConfig || loadingTemplate;

  if (isLoading) {
    return <div className="flex justify-center py-8"><WinclapLoader size={28} className="text-foreground/25" /></div>;
  }

  const handleSave = async () => {
    await saveTemplate.mutateAsync({ notificationType, htmlContent, subjectLine });
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setHtmlContent('');
    setSubjectLine('');
  };

  return (
    <div className="space-y-4">
      {/* Enable/disable toggle */}
      <Card>
        <CardContent className="flex items-center justify-between pt-4 pb-4">
          <div>
            <p className="font-medium text-sm">{t('performance.adminnotifications.notificaciones_habilitadas')}</p>
            <p className="text-xs text-muted-foreground">{t('performance.adminnotifications.activa_o_desactiva_el_envio_de')}</p>
          </div>
          <Switch
            checked={config?.enabled === true}
            onCheckedChange={(v) => setEnabled.mutateAsync({ notificationType, enabled: v })}
          />
        </CardContent>
      </Card>

      {/* Current template info */}
      {activeTemplate ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <CardTitle className="text-sm">{t('performance.adminnotifications.template_activo')}</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  Asunto: <span className="font-medium text-foreground">{activeTemplate.subjectLine}</span>
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPreview((p) => !p)}
                >
                  {preview ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {preview ? 'Ocultar' : 'Preview'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  disabled={editing}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Editar
                </Button>
              </div>
            </div>
          </CardHeader>

          {preview && (
            <CardContent className="pt-0">
              <div className="rounded-md border overflow-hidden">
                <iframe
                  srcDoc={activeTemplate.htmlContent}
                  title={t('performance.adminnotifications.email_preview')}
                  className="w-full h-96 border-0"
                  sandbox="allow-same-origin"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Variables: {'{{leaderName}}'}, {'{{startDate}}'}, {'{{endDate}}'}, {'{{clapperList}}'}
              </p>
            </CardContent>
          )}
        </Card>
      ) : (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t('performance.adminnotifications.no_hay_template_configurado')}{' '}
            <button className="underline text-primary" onClick={() => setEditing(true)}>{t('performance.adminnotifications.crear_uno')}</button>
          </CardContent>
        </Card>
      )}

      {/* Editor */}
      {editing && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{t('performance.adminnotifications.editar_template')}</CardTitle>
              <Button size="icon" aria-label={t('common.a11y.close')} variant="ghost" className="h-7 w-7" onClick={handleCancelEdit}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Asunto</Label>
              <Input className="mt-1" value={subjectLine} onChange={(e) => setSubjectLine(e.target.value)} placeholder={t('performance.adminnotifications.asunto_del_email')} />
            </div>
            <div>
              <Label>{t('performance.adminnotifications.contenido_html')}</Label>
              <Textarea
                className="mt-1 font-mono text-xs"
                rows={14}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder={t('performance.admin.notifications.html_placeholder')}
              />
            </div>
            {htmlContent && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('performance.adminnotifications.preview_del_nuevo_template')}</p>
                <div className="rounded-md border overflow-hidden">
                  <iframe
                    srcDoc={htmlContent}
                    title={t('performance.adminnotifications.preview_nuevo_template')}
                    className="w-full h-72 border-0"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saveTemplate.isPending || !htmlContent || !subjectLine}
              >
                {saveTemplate.isPending ? <WinclapLoader size={16} /> : <Save className="h-4 w-4 mr-1" />}
                {t('performance.adminnotifications.guardar_version')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function PerformanceAdminNotifications() {
  const { t } = useTranslation();

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">{t('performance.admin.layout.notifications')}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{t('performance.adminnotifications.configura_los_templates_de_email_para')}</p>
      </div>

      <Tabs defaultValue="epic_launch">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="epic_launch" className="flex-1 sm:flex-none">Lanzamiento</TabsTrigger>
          <TabsTrigger value="reminder" className="flex-1 sm:flex-none">Recordatorio</TabsTrigger>
          <TabsTrigger value="on_demand" className="flex-1 sm:flex-none">{t('performance.adminnotifications.on_demand')}</TabsTrigger>
        </TabsList>
        <TabsContent value="epic_launch" className="mt-4">
          <TemplateEditor notificationType="epic_launch" />
        </TabsContent>
        <TabsContent value="reminder" className="mt-4">
          <TemplateEditor notificationType="reminder" />
        </TabsContent>
        <TabsContent value="on_demand" className="mt-4">
          <TemplateEditor notificationType="on_demand" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
